import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { pagesApi } from './api';
import { keys } from '@/lib/query/keys';
import { useListState } from '@/lib/hooks/useListState';
import { useDebounced } from '@/lib/hooks/useDebounced';
import { useLocale } from '@/lib/i18n/useLocale';
import { resolveI18n } from '@/lib/i18n/content';
import { DataView } from '@/lib/ui/states';
import { DataTable, type Column } from '@/lib/ui/Table';
import { Pagination } from '@/lib/ui/Pagination';
import { PageHeader, MonoValue } from '@/lib/ui/Typography';
import { ContentStatusPill } from '@/lib/ui/StatusPill';
import { Select } from '@/lib/ui/Select';
import { TextInput } from '@/lib/ui/Field';
import { formatRelative } from '@/lib/format';
import type { Page } from '@/lib/api/types';

/** Pages list (spec §9.1). Pages are composed of ordered sections in the editor. */
export function PagesListPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { locale } = useLocale();

  const STATUS_OPTIONS = [
    { value: '', label: t('pages.allStatuses') },
    { value: 'draft', label: t('pages.statusDraft') },
    { value: 'scheduled', label: t('pages.statusScheduled') },
    { value: 'published', label: t('pages.statusPublished') },
    { value: 'archived', label: t('pages.statusArchived') },
  ];
  const list = useListState({ status: '' });
  const [rawSearch, setRawSearch] = useState('');
  const debounced = useDebounced(rawSearch, 300);

  const query = useQuery({
    queryKey: keys.pages.list({ ...list.params, q: debounced }),
    queryFn: () => pagesApi.list({ ...list.params, q: debounced || undefined }),
    placeholderData: (prev) => prev,
  });

  const otherLocale = locale === 'ar' ? 'en' : 'ar';

  const columns: Column<Page>[] = [
    {
      id: 'slug',
      header: t('common.slug'),
      cell: (r) => <MonoValue className="font-medium text-ink-deep">{r.slug || r.key}</MonoValue>,
    },
    {
      id: 'title',
      header: t('common.title'),
      cell: (r) => {
        const primary = resolveI18n(r.title, locale) || t('pages.untitled');
        const secondary = r.title?.[otherLocale];
        return (
          <div className="flex flex-col gap-2xs">
            <span dir={locale === 'ar' ? 'rtl' : 'ltr'} className="text-ink-deep">
              {primary}
            </span>
            {secondary && (
              <span
                dir={otherLocale === 'ar' ? 'rtl' : 'ltr'}
                className="text-small text-ink-mute"
              >
                {secondary}
              </span>
            )}
          </div>
        );
      },
    },
    {
      id: 'namespaces',
      header: t('common.namespaces'),
      cell: (r) =>
        r.namespaces?.length ? (
          <MonoValue className="text-ink-mute">{r.namespaces.join(', ')}</MonoValue>
        ) : (
          <span className="text-ink-mute">—</span>
        ),
    },
    { id: 'status', header: t('common.status'), cell: (r) => <ContentStatusPill status={r.status} /> },
    {
      id: 'updated',
      header: t('common.updated'),
      align: 'end',
      cell: (r) => <span className="text-ink-mute">{formatRelative(r.updated_at)}</span>,
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t('pages.eyebrow')}
        title={t('pages.title')}
        count={query.data?.meta?.pagination.total}
      />

      <div className="mb-md flex flex-wrap items-end gap-sm">
        <div className="w-[180px]">
          <Select
            label={t('common.status')}
            value={(list.filters.status as string) || ''}
            onValueChange={(v) => list.setFilter('status', v)}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="min-w-[180px] flex-1">
          <TextInput label={t('common.search')} value={rawSearch} onChange={(e) => setRawSearch(e.target.value)} />
        </div>
      </div>

      <DataView query={query} isEmpty={(d) => d.data.length === 0}>
        {(d) => (
          <>
            <DataTable
              columns={columns}
              rows={d.data}
              rowKey={(r) => r.id}
              onRowClick={(r) => navigate(`/pages/${r.id}`)}
            />
            {d.meta?.pagination && (
              <Pagination
                meta={d.meta.pagination}
                onPageChange={list.setPage}
                onPerPageChange={list.setPerPage}
              />
            )}
          </>
        )}
      </DataView>
    </div>
  );
}
